import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBrand, useLogoUrl } from '@/lib/brand';
import { knoxApi } from '@/lib/knox-api';
import { useAuth } from '@/lib/auth';

export function OrgBindingPage() {
  const { t } = useTranslation('knox-aiops');
  const navigate = useNavigate();
  const { logout } = useAuth();
  const brand = useBrand();
  const logoUrl = useLogoUrl();
  const [status, setStatus] = useState<'idle' | 'joining' | 'redirecting'>('idle');

  useEffect(() => {
    const joinToken = localStorage.getItem('pending_join_token');
    const inviteToken = localStorage.getItem('pending_invite_token');

    if (joinToken) {
      localStorage.removeItem('pending_join_token');
      localStorage.removeItem('pending_invite_token');
      setStatus('joining');
      knoxApi.joinOrgLoggedIn({ token: joinToken })
        .then(() => window.location.href = '/workspaces')
        .catch(() => setStatus('idle'));
    } else if (inviteToken) {
      localStorage.removeItem('pending_invite_token');
      setStatus('redirecting');
      navigate(`/invite`, { state: { token: inviteToken } });
    }
  }, [navigate]);

  if (status === 'joining') {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">{t('invite.joining')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-white font-[Inter,sans-serif] text-foreground">
      <header className="flex shrink-0 w-full items-center justify-between px-6 py-3 bg-white/80 backdrop-blur-md border-b border-black/[0.04]">
        <div className="flex items-center gap-2">
          <img src={logoUrl} alt={brand} className="h-7 w-auto" />
          <span className="font-heading text-xl font-extrabold tracking-tighter text-foreground">{brand}</span>
        </div>
        <button
          onClick={logout}
          className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-black/[0.06] hover:text-foreground transition-all"
        >
          <span className="material-symbols-outlined text-base">logout</span>
          {t('nav.signOut')}
        </button>
      </header>

      <main className="relative flex flex-1 min-h-0 flex-col items-center justify-center overflow-auto px-4 py-6">
        <div className="knox-dot-grid pointer-events-none absolute inset-0 opacity-10" />
        <div className="knox-card-glass relative w-full max-w-[420px] p-6 sm:p-8">
          <div className="mb-6 sm:mb-8 text-center">
            <div className="mb-3 flex justify-center">
              <img src={logoUrl} alt="" className="h-10 sm:h-12 w-auto" />
            </div>
            <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foreground mb-1">
              {t('orgBinding.title')}
            </h1>
            <p className="text-sm text-muted-foreground">{t('orgBinding.subtitle')}</p>
          </div>

          <div className="flex flex-col gap-4">
            <button
              type="button"
              onClick={() => navigate('/invite')}
              className="flex items-center justify-center gap-3 w-full rounded-xl bg-gradient-to-br from-[#0070F3] to-[#005ac6] px-5 py-4 text-sm font-bold text-white shadow-lg transition-all duration-300 hover:shadow-[0_0_20px_rgba(135,173,255,0.3)] active:scale-[0.98]"
            >
              <span className="material-symbols-outlined text-xl">apartment</span>
              {t('orgBinding.createLabel')}
            </button>

            <button
              type="button"
              onClick={() => navigate('/join')}
              className="flex items-center justify-center gap-3 w-full rounded-xl border-2 border-black/[0.12] bg-surface-container-lowest px-5 py-4 text-sm font-bold text-foreground transition-all duration-300 hover:border-primary/30 hover:bg-primary/5 active:scale-[0.98]"
            >
              <span className="material-symbols-outlined text-xl">group_add</span>
              {t('orgBinding.joinLabel')}
            </button>
          </div>
        </div>
      </main>

      <footer className="shrink-0 w-full bg-white">
        <div className="mx-auto flex w-full max-w-7xl flex-col items-center gap-2 px-8 py-4">
          <span className="text-[0.6875rem] uppercase tracking-widest text-muted-foreground/60">{t('login.copyright')}</span>
          <div className="flex gap-6">
            <a href="/terms" className="text-[0.6875rem] uppercase tracking-widest text-muted-foreground/40 transition-colors hover:text-primary hover:underline underline-offset-4">{t('login.termsOfService')}</a>
            <a href="/privacy" className="text-[0.6875rem] uppercase tracking-widest text-muted-foreground/40 transition-colors hover:text-primary hover:underline underline-offset-4">{t('login.privacyPolicy')}</a>
            <a href="/refund" className="text-[0.6875rem] uppercase tracking-widest text-muted-foreground/40 transition-colors hover:text-primary hover:underline underline-offset-4">{t('login.refundPolicy')}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
